<!-- Unified Code (DGA) footer, DarkGreen variant (criterion 27). Custom mode renders #customFooter unchanged. -->
<footer *ngIf="unified; else customFooter" class="uc-footer">
  <div class="uc-footer__inner">
    <div class="uc-footer__top">
      <a class="uc-footer__brand" routerLink="/home">
        <img class="uc-footer__logo" loading="lazy" [src]="footerLogo" [alt]="organizationName" />
      </a>
      <p class="uc-footer__org uc-text-lg uc-semibold mb-0">{{ organizationName }}</p>
    </div>

    <div class="uc-footer__groups">
      <section *ngFor="let group of footerGroups; trackBy: trackById" class="uc-footer__group"
        [attr.aria-labelledby]="'uc-footer-group-' + group.id">
        <h2 [id]="'uc-footer-group-' + group.id" class="uc-footer__heading uc-text-md uc-semibold">
          {{ group.headingKey ? (group.headingKey | translate) : groupHeading(group) }}
        </h2>
        <ul class="uc-footer__links">
          <li *ngFor="let link of group.links; trackBy: trackById">
            <a *ngIf="link.url" class="uc-footer__link" [routerLink]="link.url" routerLinkActive="uc-footer__link--selected"
              ariaCurrentWhenActive="page">{{ link.labelKey ? (link.labelKey | translate) : linkText(link) }}</a>
            <a *ngIf="link.href" class="uc-footer__link" [href]="link.href" target="_blank" data-uc-external-icon rel="noopener">
              {{ link.labelKey ? (link.labelKey | translate) : linkText(link) }}
              <i class="fa-solid fa-arrow-up-right-from-square uc-external-icon" aria-hidden="true"></i>
            </a>
          </li>
        </ul>
      </section>

      <section *ngIf="hasContactInfo" class="uc-footer__group" aria-labelledby="uc-footer-group-contact">
        <h2 id="uc-footer-group-contact" class="uc-footer__heading uc-text-md uc-semibold">
          {{ 'unifiedShell.footer.contactInfo' | translate }}
        </h2>
        <address class="uc-footer__contact mb-0">
          <p *ngIf="address" class="uc-footer__contact-item mb-0">
            <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
            <span class="visually-hidden">{{ 'unifiedShell.footer.address' | translate }}</span>
            <span>{{ address }}</span>
          </p>
          <p *ngFor="let email of emails" class="uc-footer__contact-item mb-0">
            <i class="fa-regular fa-envelope" aria-hidden="true"></i>
            <span class="visually-hidden">{{ 'unifiedShell.footer.email' | translate }}</span>
            <a class="uc-footer__link" [href]="'mailto:' + email"><bdi>{{ email }}</bdi></a>
          </p>
          <p *ngFor="let phone of phones" class="uc-footer__contact-item mb-0">
            <i class="fa-solid fa-phone" aria-hidden="true"></i>
            <span class="visually-hidden">{{ 'unifiedShell.footer.phone' | translate }}</span>
            <a class="uc-footer__link" [href]="'tel:' + phoneHref(phone)"><bdi dir="ltr">{{ phone }}</bdi></a>
          </p>
        </address>
      </section>
    </div>

    <div *ngIf="socialLinks.length > 0 || identityModel.governmentLogo" class="uc-footer__middle">
      <ul *ngIf="socialLinks.length > 0" class="uc-footer__social" [attr.aria-label]="'unifiedShell.footer.socialMedia' | translate">
        <li *ngFor="let social of socialLinks">
          <a class="uc-footer__social-link" [href]="social.url" target="_blank" data-uc-external-icon rel="noopener"
            [attr.aria-label]="(social.labelKey | translate) + ' - ' + ('unifiedShell.a11y.opensInNewTab' | translate)">
            <i class="fa-brands" [ngClass]="social.icon" aria-hidden="true"></i>
          </a>
        </li>
      </ul>
      <img *ngIf="identityModel.governmentLogo" class="uc-footer__gov-logo" loading="lazy"
        [src]="identityPrefix + identityModel.governmentLogo" [alt]="'unifiedShell.header.governmentLogoAlt' | translate" />
    </div>

    <div class="uc-footer__bottom">
      <p class="uc-footer__copyright uc-text-sm mb-0">{{ copyright }}</p>
      <button type="button" class="uc-footer__lang" (click)="changeLanguage()">
        <i class="fa-solid fa-globe" aria-hidden="true"></i>
        <span [attr.lang]="isRtl ? 'en' : 'ar'">{{ isRtl ? 'English' : 'العربية' }}</span>
      </button>
    </div>
  </div>
</footer>

<ng-template #customFooter>
<footer class="position-relative">
  <div class="container py-4 position-relative">
    <div class="d-flex flex-wrap flex-column flex-lg-row align-items-center justify-content-center align-items-lg-start justify-content-lg-between">
      <div class="d-flex align-items-center">
        <img loading="lazy" [src]="identityModel.coloredImage ? identityPrefix + identityModel.coloredImage : 'assets/imgs/EXAB.svg'" [alt]="identityModel.organizationName || identityModel.organizationNameAr"/>
        <p class="mb-0" translate="footer.providedBy"></p>
      </div>
      <ul class="d-flex flex-wrap align-items-center justify-content-center mt-2 mt-lg-0">
        <li><a [routerLink]="['/about']" translate="footer.aboutUs"></a></li>
        <li *ngIf="showPage(pageType.Policy) showPrivacy">
          <a [routerLink]="['/privacy']" translate="footer.privacy"></a>
        </li>
        <li *ngIf="showPage(pageType.Terms)">
          <a [routerLink]="['/terms']" translate="footer.terms"></a>
        </li>
        <li>
          <a [routerLink]="['/contact']" translate="footer.contact"></a>
        </li>
        <li *ngIf="showPage(pageType.UserGuide)">
          <a [routerLink]="['/user-guide']" translate="footer.userGuide"></a>
        </li>
        <li *ngIf="showPage(pageType.TraineeGuide)">
          <a [routerLink]="['/trainee-guide']" translate="footer.traineeGuide"></a>
        </li>

        <!-- <li *ngIf="showPage(pageType.OurTeam)">
          <a [routerLink]="['/our-team']" translate="footer.ourTeam"></a>
        </li> -->
        <li *ngFor="let parent of getCustomParentPages()">
          <div class="dropup-center dropup">
            <a class="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">{{isRtl? parent.titleAr : parent.title}}</a>
            <ul class="dropdown-menu" *ngFor="let child of getCustomChildPages(parent.id)">
              <li class="m-0"><a class="dropdown-item" [routerLink]="['/page/'+child.id]">{{isRtl? child.titleAr : child.title}}</a></li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
    <div class="line my-3"></div>
    <p class="text-center mb-0">
      {{
        isRtl
          ? identityModel.footerAr
            ? identityModel.footerAr
            : ("footer.rights" | translate)
          : identityModel.footer
          ? identityModel.footer
          : ("footer.rights" | translate)
      }}
    </p>
  </div>
  <span class="position-absolute"></span>
</footer>
</ng-template>
